import { useState } from "react";
import { useSwipeable } from "react-swipeable";
import { TimelineDateBars } from "./TimelineDateBars";
import { setSelectedTime } from "../../../features/surface/surfaceSlice";
import { useAppDispatch } from "../../../app/hooks";
import { MS_IN_DAY } from "../geoUtils";
import {
    setCurrentDay,
    useCurrentDay,
    useMaxDataTime,
    useMaxDisplayTime,
    useMinDataTime,
    useMinDisplayTime
} from "../../../features/time/TimelineSlice";
import { useIsMobile } from "../../../features/pageWidth/pageWidthSlice";

enum SwipePositions
    {
    PREVIOUS_DAY = 0,
    CURRENT_DAY = -100,
    NEXT_DAY = -200,
}

const TimelineDateBarsSwipeable: React.FC = ( ) =>
{
    const dispatch = useAppDispatch();
    const minDisplayTime = useMinDisplayTime();
    const minDataTime = useMinDataTime();
    const maxDisplayTime = useMaxDisplayTime();
    const maxDataTime = useMaxDataTime();
    const currentDay = useCurrentDay();
    const isMobile = useIsMobile();
    const [ swipePositionPercent, setSwipePositionPercent ] = useState( SwipePositions.CURRENT_DAY );
    const [ swipeTransition, setSwipeTransition ] = useState( "transform 0.3s ease-out" );
    const dateIndex = Math.ceil( ( currentDay - minDataTime ) / MS_IN_DAY ) + 1;

    const handleDayChange = (
        nextOrPrevDay: number,
        timelineMaxDataTime: number,
        timelineMinDataTime: number,
        direction: "next" | "prev"
    ) =>
    {
        if (
            ( direction === "next" && nextOrPrevDay <= timelineMaxDataTime ) ||
            ( direction === "prev" &&
                ( nextOrPrevDay >= timelineMinDataTime ||
                    new Date( nextOrPrevDay ).getDate() === new Date( timelineMinDataTime ).getDate() ) )
        )
        {
            setSwipeTransition( "transform 0.3s ease-out" );
            setSwipePositionPercent( direction === "next" ? SwipePositions.NEXT_DAY : SwipePositions.PREVIOUS_DAY );
            setTimeout( () =>
            {
                setSwipeTransition( "none" );
                setSwipePositionPercent( SwipePositions.CURRENT_DAY );
                dispatch( setCurrentDay( nextOrPrevDay ) );
                dispatch(
                    setSelectedTime(
                        nextOrPrevDay + ( direction === "prev" ? MS_IN_DAY - 1 : 0 )
                    )
                );
            }, 300 );
        }
    };

    const handleNextDay = () =>
    {
        handleDayChange( currentDay + MS_IN_DAY, maxDataTime, minDataTime, "next" );
    };

    const handlePreviousDay = () =>
    {
        handleDayChange( currentDay - MS_IN_DAY, maxDataTime, minDataTime, "prev" );
    };

    const handlers = useSwipeable( {
        onSwipedLeft: handleNextDay,
        onSwipedRight: handlePreviousDay,
        trackMouse: true,
        preventScrollOnSwipe: true,
    } );

    return (
        <div style = {{ overflow: "hidden", paddingRight: isMobile ? "2px" : "0px" }}>
            <div {...handlers}>
                <div
                    style = {{
                        display: "flex",
                        transform: `translateX(${swipePositionPercent}%)`,
                        transition: swipeTransition,
                    }}
                >
                    <div style = {{ flexShrink: 0, width: "100%", paddingRight: "2px" }}>
                        <TimelineDateBars
                            minDisplayTime = {Math.max( minDisplayTime - MS_IN_DAY, minDataTime )}
                            maxDisplayTime = {maxDisplayTime - MS_IN_DAY}
                            dayNumberOverride = {dateIndex - 1}
                            overrideDoNotDisplayRightBar

                        />
                    </div>
                    <div style = {{ flexShrink: 0, width: "100%" }}>
                        <TimelineDateBars
                            minDisplayTime = {minDisplayTime}
                            maxDisplayTime = {maxDisplayTime}
                            dayNumberOverride = {dateIndex}
                        />
                    </div>
                    <div style = {{ flexShrink: 0, width: "100%", paddingLeft: "2px" }}>
                        <TimelineDateBars
                            minDisplayTime = {minDisplayTime + MS_IN_DAY}
                            maxDisplayTime = {Math.min( maxDisplayTime + MS_IN_DAY, maxDataTime )}
                            dayNumberOverride = {dateIndex + 1}
                            overrideDoNotDisplayLeftBar
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TimelineDateBarsSwipeable;
